import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { fromJS } from "immutable";
import SelectField from "../../../../components/ui/SelectField";
import InputField from "../../../../components/ui/InputField";
import ColorField from "../../../../components/ui/ColorField";
import { useDispatch, useSelector } from "react-redux";
import ReportSettingAction from "../../../../actions/ReportSettingAction";
import useDebounce from "../../../../useDebounce";
import {
  colorCodes,
  colors as defaultColors,
} from "../../../../board/components/TaskDetail/ColorPicker";
import { iconList } from "../../../ReportSetting";
import { dateUnitFields, measureByFields, segmentByFields, viewByFields } from "../../../DataUtils";
import { fetchAggregationDimensions } from "../../../../api/DataApi";

type LocalOption = {
  value: string;
  label: string;
  description?: React.ReactNode;
};

// ─── Shared hook for report data ────────────────────────────────
const useReportData = () => {
  const dispatch = useDispatch();
  const reportData = useSelector((state: any) => state.reportSetting.get("data"));
  const ruleReducer = useSelector((state: any) => state.ruleReducer);

  const onChange = (key: string, value: any) => {
    const data = reportData.toJS();
    const colors =
      (reportData.get("colors")?.toJS() || []).length > 0
        ? reportData.get("colors")?.toJS()
        : defaultColors;

    let keyData = key;
    let valueData = value;
    if (keyData === "viewBy") {
      keyData = "nameKey";
    } else if (keyData === "measureBy") {
      keyData = "valueKey";
    } else if (keyData === "lineColorCurrent") {
      keyData = "colors";
      valueData = [value, colors[1]];
    } else if (keyData === "lineColorPrevious") {
      keyData = "colors";
      valueData = [colors[0], value];
    } else if (keyData === "goalColor") {
      keyData = "colors";
      valueData = [value];
    } else if (keyData === "scorecardColor") {
      keyData = "colors";
      valueData = [value];
    } else if (keyData === "icons") {
      keyData = "icons";
      valueData = [value];
    }
    dispatch(
      ReportSettingAction.settingDataReport({
        ...data,
        [keyData]: valueData,
      })
    );
  };

  return { reportData, ruleReducer, onChange, dispatch };
};

const findLabel = (opts: LocalOption[], value: string): string => {
  return opts.find((o) => String(o.value) === String(value))?.label || "";
};

// ─── MetricSection ──────────────────────────────────────────────
export const MetricSection = ({
  chartType,
  objectType,
}: {
  chartType?: string;
  objectType?: string;
}) => {
  const { t } = useTranslation();
  const { reportData, onChange } = useReportData();
  const measureBy = reportData.get("valueKey");

  const measureOptions: LocalOption[] = useMemo(
    () => (measureByFields[objectType || "SALE"] || []) as unknown as LocalOption[],
    [objectType]
  );

  if (chartType === "Goal") return null;

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t("Metric")}</div>
      <SelectField<LocalOption>
        placeholder={t("Select measure")}
        value={{
          value: measureBy,
          label: findLabel(measureOptions, measureBy),
        }}
        options={measureOptions}
        onChange={(opt) => onChange("measureBy", (opt as any)?.value)}
        stylesOverride={{
          control: (base) => ({
            ...base,
            minHeight: 32,
            height: 32,
            borderRadius: 8,
            borderColor: "#d1d5dc",
            boxShadow: "none",
            ":hover": { borderColor: "#d1d5db" },
          }),
        }}
      />
    </div>
  );
};

// ─── GroupedBySection ───────────────────────────────────────────
export const GroupedBySection = ({
  chartType,
  objectType,
  projectId,
}: {
  chartType?: string;
  objectType?: string;
  projectId?: string;
}) => {
  const { t } = useTranslation();
  const { reportData, onChange } = useReportData();
  const viewBy = reportData.get("nameKey");
  const dateUnit = reportData.get("dateUnit");
  const typeKey = objectType || "SALE";

  const fallbackViewOptions: LocalOption[] = useMemo(
    () => (viewByFields[typeKey] || []) as unknown as LocalOption[],
    [typeKey]
  );

  const { data: dimensions, isLoading } = useQuery({
    queryKey: ["aggregationDimensions", projectId, typeKey],
    queryFn: () => fetchAggregationDimensions(projectId!, typeKey),
    enabled: Boolean(projectId),
    staleTime: 5 * 60 * 1000,
  });

  const viewOptions: LocalOption[] = useMemo(() => {
    if (dimensions?.groupByFields?.length) {
      return dimensions.groupByFields.map((f) => ({
        value: f.fieldId,
        label: f.label,
      }));
    }
    return fallbackViewOptions;
  }, [dimensions?.groupByFields, fallbackViewOptions]);

  const selectedGroupMeta = dimensions?.groupByFields?.find((f) => f.fieldId === viewBy);
  const showDateUnit =
    selectedGroupMeta?.dataType === "datetime" ||
    viewBy === "start" ||
    viewBy === "end" ||
    viewBy === "finish";

  const dateUnitOptions: LocalOption[] = useMemo(
    () => dateUnitFields as unknown as LocalOption[],
    []
  );

  if (chartType === "Goal" || chartType === "Pie" || chartType === "Number") return null;

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        {t("Grouped by")}
      </div>
      <SelectField<LocalOption>
        placeholder={isLoading ? t("Loading…") : t("Select view")}
        isDisabled={Boolean(projectId) && isLoading}
        value={{
          value: viewBy,
          label: findLabel(viewOptions, viewBy),
        }}
        options={viewOptions}
        onChange={(opt) => onChange("viewBy", (opt as any)?.value)}
        stylesOverride={{
          control: (base) => ({
            ...base,
            minHeight: 32,
            height: 32,
            borderRadius: 8,
            borderColor: "#d1d5dc",
            boxShadow: "none",
            ":hover": { borderColor: "#d1d5db" },
          }),
        }}
      />
      {showDateUnit && (
        <>
          <div className="text-xs font-medium text-gray-400 mt-1">{t("Date Unit")}</div>
          <SelectField<LocalOption>
            placeholder={t("Select unit")}
            value={{
              value: dateUnit,
              label: findLabel(dateUnitOptions, dateUnit),
            }}
            options={dateUnitOptions}
            onChange={(opt) => onChange("dateUnit", (opt as any)?.value)}
            stylesOverride={{
              control: (base) => ({
                ...base,
                minHeight: 32,
                height: 32,
                borderRadius: 8,
                borderColor: "#d1d5dc",
                boxShadow: "none",
                ":hover": { borderColor: "#d1d5db" },
              }),
            }}
          />
        </>
      )}
    </div>
  );
};

// ─── SegmentBySection ──────────────────────────────────────────
export const SegmentBySection = ({
  chartType,
  objectType,
  projectId,
}: {
  chartType?: string;
  objectType?: string;
  projectId?: string;
}) => {
  const { t } = useTranslation();
  const { reportData, onChange } = useReportData();
  const segmentBy = reportData.get("segmentBy");
  const typeKey = objectType || "SALE";

  const fallbackSegmentOptions: LocalOption[] = useMemo(
    () => (segmentByFields[typeKey] || []) as unknown as LocalOption[],
    [typeKey]
  );

  const { data: dimensions, isLoading } = useQuery({
    queryKey: ["aggregationDimensions", projectId, typeKey],
    queryFn: () => fetchAggregationDimensions(projectId!, typeKey),
    enabled: Boolean(projectId),
    staleTime: 5 * 60 * 1000,
  });

  const segmentOptions: LocalOption[] = useMemo(() => {
    const none: LocalOption = { value: "None", label: t("None") };
    if (dimensions?.segmentByFields?.length) {
      const fromApi = dimensions.segmentByFields.map((f) => ({
        value: f.fieldId,
        label: f.label,
      }));
      return [none, ...fromApi];
    }
    return [none, ...fallbackSegmentOptions];
  }, [dimensions?.segmentByFields, fallbackSegmentOptions, t]);

  if (chartType === "Goal" || chartType === "Number") return null;

  return (
    <SelectField<LocalOption>
      label={t("Segment by")}
      placeholder={isLoading && projectId ? t("Loading…") : t("Select segment")}
      isDisabled={Boolean(projectId) && isLoading}
      value={{
        value: segmentBy,
        label: findLabel(segmentOptions, segmentBy),
      }}
      options={segmentOptions}
      onChange={(opt) => onChange("segmentBy", (opt as any)?.value)}
      stylesOverride={{
        control: (base) => ({
          ...base,
          minHeight: 32,
          height: 32,
          borderRadius: 8,
          borderColor: "#d1d5dc",
          boxShadow: "none",
          ":hover": { borderColor: "#d1d5db" },
        }),
      }}
    />
  );
};

// ─── AppearanceSection ─────────────────────────────────────────
export const AppearanceSection = ({ chartType }: { chartType?: string }) => {
  const { t } = useTranslation();
  const { reportData, onChange, ruleReducer, dispatch } = useReportData();

  const viewBy = reportData.get("nameKey");
  const segmentBy = reportData.get("segmentBy");
  const comparisonBy = reportData.get("comparisonBy");
  const icons =
    (reportData.get("icons")?.toJS() || []).length > 0
      ? reportData.get("icons")?.toJS()
      : ["RateIcon"];
  const colors =
    (reportData.get("colors")?.toJS() || []).length > 0
      ? reportData.get("colors")?.toJS()
      : defaultColors;

  const groups = ruleReducer.get(viewBy) || fromJS([]);
  const segments = ruleReducer.get(segmentBy) || fromJS([{ label: t("None"), value: "None" }]);
  const groupsList = segmentBy == "None" ? groups : segments;

  const [localTitle, setLocalTitle] = useState("");
  const [localSubtitle, setLocalSubtitle] = useState("");

  useEffect(() => {
    setLocalTitle(reportData.get("title"));
    setLocalSubtitle(reportData.get("subtitle"));
  }, [reportData]);

  useDebounce(
    () => {
      onChange("title", localTitle);
    },
    [localTitle],
    200
  );
  useDebounce(
    () => {
      onChange("subtitle", localSubtitle);
    },
    [localSubtitle],
    200
  );

  const comparisonOptions: LocalOption[] = useMemo(
    () => [
      { value: "lastYear", label: t("Last Year") },
      { value: "lastMonth", label: t("Last Month") },
      { value: "lastWeek", label: t("Last Week") },
      { value: "lastQuarter", label: t("Last Quarter") },
      { value: "none", label: t("None") },
    ],
    [t]
  );

  const getItemColor = (colorCode, index) => {
    return colorCodes.find((item) => item.code == colorCode) || colorCodes[index];
  };

  const onChangeColor = (index: number, value: any) => {
    const valueData = groupsList.map((_item, idx) => colors[idx]).toJS();
    valueData[index] = value;
    dispatch(
      ReportSettingAction.settingDataReport({
        ...reportData.toJS(),
        colors: valueData,
      })
    );
  };

  return (
    <div className="space-y-4">
      {/* Title & Subtitle */}
      <InputField
        label={t("Title")}
        value={localTitle}
        onChange={setLocalTitle}
        placeholder={t("e.g Deal Performance")}
      />
      <InputField
        label={t("Subtitle")}
        value={localSubtitle}
        onChange={setLocalSubtitle}
        placeholder={t("e.g Last year")}
      />

      {/* Line chart: Stack By + Color */}
      {chartType === "Line" && (
        <div className="space-y-3">
          <SelectField<LocalOption>
            label={t("Stack By")}
            placeholder={t("Select stack by")}
            value={{
              value: comparisonBy,
              label: findLabel(comparisonOptions, comparisonBy),
            }}
            options={comparisonOptions}
            onChange={(opt) => onChange("comparisonBy", (opt as any)?.value)}
          />
          <div className="text-sm font-semibold text-gray-800">{t("Color")}</div>
          <div className="grid grid-cols-1 gap-3">
            <ColorField
              label={t("Current line")}
              value={getItemColor(colors[0], 0).code}
              onChange={(value) => onChange("lineColorCurrent", value)}
              placeholder={t("Select current line color")}
            />
            <ColorField
              label={t("Previous line")}
              value={getItemColor(colors[1], 7).code}
              onChange={(value) => onChange("lineColorPrevious", value)}
              placeholder={t("Select previous line color")}
            />
          </div>
        </div>
      )}

      {/* Goal chart: Color */}
      {chartType === "Goal" && (
        <div className="space-y-3">
          <div className="text-sm font-semibold text-gray-800">{t("Color")}</div>
          <div className="grid grid-cols-1 gap-3">
            <ColorField
              value={getItemColor(colors[0], 0).code}
              onChange={(value) => onChange("goalColor", value)}
              placeholder={t("Select color")}
            />
          </div>
        </div>
      )}

      {/* Number chart: Icon + Color */}
      {chartType === "Number" && (
        <div className="space-y-3">
          <SelectField<LocalOption>
            label={t("Icon")}
            placeholder={t("Select icon")}
            value={iconList.find((item) => item.value === icons[0])}
            options={iconList}
            onChange={(opt) => onChange("icons", (opt as any)?.value)}
            formatOptionLabel={(option) => (
              <div
                className="w-full hover:text-blue-600 flex items-center space-x-2"
                key={option.value}
              >
                <div>{option.description}</div>
                <div>{option.label}</div>
              </div>
            )}
          />
          <div className="text-sm font-semibold text-gray-800">{t("Color")}</div>
          <div className="grid grid-cols-1 gap-3">
            <ColorField
              value={getItemColor(colors[0], 0).code}
              onChange={(value) => onChange("scorecardColor", value)}
              placeholder={t("Select color")}
            />
          </div>
        </div>
      )}

      {/* Bar/Column/Pie: Color per group */}
      {(chartType === "Bar" || chartType === "Column" || chartType === "Pie") && (
        <div className="space-y-3">
          <div className="text-sm font-semibold text-gray-800">{t("Color")}</div>
          <div className="space-y-3">
            {groupsList.map((item: any, index: number) => (
              <ColorField
                key={item.get("value")}
                label={item.get("label")}
                value={colors[index]}
                onChange={(value) => onChangeColor(index, value)}
                placeholder={t("Select color")}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Default export (backward compat) ──────────────────────────
type Props = {
  objectType: string;
  chartType?: string;
  projectId?: string;
};

const SettingTabContent = ({ objectType, chartType, projectId }: Props) => {
  return (
    <div className="space-y-5">
      <MetricSection chartType={chartType} objectType={objectType} />
      <GroupedBySection chartType={chartType} objectType={objectType} projectId={projectId} />
      <SegmentBySection chartType={chartType} objectType={objectType} projectId={projectId} />
      <AppearanceSection chartType={chartType} />
    </div>
  );
};

export default SettingTabContent;
